import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { PodsService, ModernizationPodDto } from '../../services/administration/pods.service';

import { FormsModule } from '@angular/forms';
import { MaterialModule } from '../../material.module';
import { LeftMenuComponent } from '../../menu/left-menu/left-menu.component';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { PodsListSkeletonComponent } from './pods-list-skeleton/pods-list-skeleton.component';
import { PodsListTableSkeletonComponent } from './pods-list-table-skeleton/pods-list-table-skeleton.component';

interface ColumnDef {
  key: string;
  label: string;
}

@Component({
  selector: 'modernization-pods-list',
  templateUrl: './pods-list.component.html',
  styleUrls: ['./pods-list.component.scss'],
  imports: [FormsModule, MaterialModule, LeftMenuComponent, SpinnerComponent, PodsListSkeletonComponent, PodsListTableSkeletonComponent]
})
export class PodsListComponent implements OnInit {
  @Input() embedded = false;

  pageLoading = false;
  tableLoading = false;
  isLoading = false;
  error: string | null = null;

  displayedColumns: ColumnDef[] = [
    { key: 'podId', label: 'ID' },
    { key: 'name', label: 'Name' },
    { key: 'isVisible', label: 'Visible' },
    { key: 'actions', label: 'Actions' },
  ];

  displayedColumnsKeys = this.displayedColumns.map((c) => c.key);
  pods = new MatTableDataSource<ModernizationPodDto>([]);

  // Inline edit/create state
  editPodId: number | null = null;
  editName = '';
  editIsVisible = true;

  createName = '';
  createIsVisible = true;

  private paginator: MatPaginator | null = null;
  private sort: MatSort | null = null;

  @ViewChild(MatPaginator)
  set matPaginator(paginator: MatPaginator) {
    this.paginator = paginator;
    this.applyTableFeatures();
  }

  @ViewChild(MatSort)
  set matSort(sort: MatSort) {
    this.sort = sort;
    this.applyTableFeatures();
  }

  constructor(private podsService: PodsService) {}

  ngOnInit(): void {
    this.pods.sortingDataAccessor = (item: any, property: string) => {
      const raw = item?.[property];
      if (raw === null || raw === undefined) {
        return '';
      }
      if (typeof raw === 'boolean') {
        return raw ? 1 : 0;
      }
      if (typeof raw === 'number') {
        return raw;
      }
      return String(raw).toLowerCase();
    };

    this.loadPods(true);
  }

  private applyTableFeatures(): void {
    if (this.paginator) {
      this.pods.paginator = this.paginator;
    }
    if (this.sort) {
      this.pods.sort = this.sort;
    }
  }

  loadPods(isInitialLoad = false): void {
    if (isInitialLoad) {
      this.pageLoading = true;
    } else {
      this.tableLoading = true;
    }
    this.error = null;

    this.podsService.getAll().subscribe({
      next: (pods) => {
        this.pods.data = pods ?? [];
        if (isInitialLoad) {
          this.pageLoading = false;
        } else {
          this.tableLoading = false;
          this.isLoading = false;
        }
        this.cancelEdit();
      },
      error: (err) => {
        // eslint-disable-next-line no-console
        console.error('Error loading pods:', err);
        this.error = 'Failed to load PODs list';
        if (isInitialLoad) {
          this.pageLoading = false;
        } else {
          this.tableLoading = false;
          this.isLoading = false;
        }
      },
    });
  }

  startEdit(pod: ModernizationPodDto): void {
    this.editPodId = pod.podId;
    this.editName = pod.name;
    this.editIsVisible = pod.isVisible;
  }

  cancelEdit(): void {
    this.editPodId = null;
    this.editName = '';
    this.editIsVisible = true;
  }

  saveEdit(): void {
    if (!this.editPodId) {
      return;
    }
    const name = (this.editName ?? '').trim();
    if (!name) {
      this.error = 'Name is required';
      return;
    }

    this.isLoading = true;
    this.error = null;

    this.podsService.update(this.editPodId, { name, isVisible: this.editIsVisible }).subscribe({
      next: () => this.loadPods(),
      error: (err) => {
        // eslint-disable-next-line no-console
        console.error('Error updating pod:', err);
        this.error = 'Failed to update POD';
        this.isLoading = false;
      },
    });
  }

  createPod(): void {
    const name = (this.createName ?? '').trim();
    if (!name) {
      this.error = 'Name is required';
      return;
    }

    this.isLoading = true;
    this.error = null;

    this.podsService.create({ name, isVisible: this.createIsVisible }).subscribe({
      next: () => {
        this.createName = '';
        this.createIsVisible = true;
        this.loadPods();
      },
      error: (err) => {
        // eslint-disable-next-line no-console
        console.error('Error creating pod:', err);
        this.error = 'Failed to create POD';
        this.isLoading = false;
      },
    });
  }

  deletePod(podId: number): void {
    this.isLoading = true;
    this.error = null;

    this.podsService.delete(podId).subscribe({
      next: () => this.loadPods(),
      error: (err) => {
        // eslint-disable-next-line no-console
        console.error('Error deleting pod:', err);
        this.error = 'Failed to delete POD';
        this.isLoading = false;
      },
    });
  }
}
